import "@/App.css";
import { motion } from "framer-motion";
import { MapPin, Phone, Star, Clock, Coffee, Navigation, ChevronRight, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import AdminPage from "./pages/AdminPage";
import MenuPage from "./pages/MenuPage";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

// Business Info
const BUSINESS_INFO = {
  name: "Rott Coffee Co",
  tagline: "Ayrancı'da Nitelikli Kahve Deneyimi",
  rating: 4.9,
  reviewCount: 113,
  priceRange: "₺200–400",
  address: "Ayrancı, Şht. Mahir Turan Sk. No:8, 06540 Çankaya / Ankara",
  phone: "0531 598 33 60",
  hours: "08:00 - 00:00",
  instagramUrl: "https://www.instagram.com/rottcoffeeco/",
  googleMapsUrl: "https://www.google.com/maps/dir/?api=1&destination=Ayrancı,+Şht.+Mahir+Turan+Sk.+No:8,+06540+Çankaya+Ankara",
  googleMapsEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3060.5!2d32.85!3d39.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDU0JzAwLjAiTiAzMsKwNTEnMDAuMCJF!5e0!3m2!1str!2str!4v1"
};

// Images
const IMAGES = {
  hero: "https://images.unsplash.com/photo-1738597966728-349bf6247848?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920",
  about: "/rott_about_new.jpg",
  texture: "https://images.unsplash.com/photo-1763473821509-9a383b480844?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
};

// Reviews data
const REVIEWS = [
  {
    id: 1,
    name: "Elif Y.",
    rating: 5,
    text: "Ankara'da içtiğim en iyi kahve! Atmosfer muhteşem, personel çok ilgili. Kesinlikle tekrar geleceğim.",
    date: "2 hafta önce"
  },
  {
    id: 2,
    name: "Mehmet K.",
    rating: 5,
    text: "Özel harmanları denemeden geçmeyin. Latte art konusunda da çok başarılılar. Tavsiye ederim.",
    date: "1 ay önce"
  },
  {
    id: 3,
    name: "Ayşe D.",
    rating: 5,
    text: "Ayrancı'nın en şık kahve dükkanı. Çalışma saatleri de çok uygun, gece geç saatlere kadar açık.",
    date: "3 hafta önce"
  },
  {
    id: 4,
    name: "Can B.",
    rating: 5,
    text: "Premium kahve deneyimi arıyorsanız doğru adres. Fiyatlar makul, kalite üst düzey.",
    date: "1 hafta önce"
  },
  {
    id: 5,
    name: "Zeynep A.",
    rating: 5,
    text: "Kahve kalitesi ve sunumu mükemmel. Özellikle V60 ile demlenen filtre kahveleri denemenizi öneririm.",
    date: "5 gün önce"
  },
  {
    id: 6,
    name: "Burak S.",
    rating: 5,
    text: "Haftada en az 3 kez uğruyorum. Baristalar işinin ehli, her seferinde aynı kalitede kahve alıyorsunuz.",
    date: "1 hafta önce"
  },
  {
    id: 7,
    name: "Selin T.",
    rating: 5,
    text: "Çalışmak için harika bir mekan. Wi-Fi hızlı, ortam sakin ve kahveler muhteşem. Favori kahve dükkanım!",
    date: "2 hafta önce"
  },
  {
    id: 8,
    name: "Emre K.",
    rating: 5,
    text: "Arkadaşlarımla buluşmak için ideal. Sıcak atmosfer, güler yüzlü personel ve lezzetli kahveler.",
    date: "3 gün önce"
  },
  {
    id: 9,
    name: "Deniz M.",
    rating: 5,
    text: "Espresso bazlı içeceklerde Ankara'nın en iyisi. Flat white'ları kaçırmayın!",
    date: "1 ay önce"
  },
  {
    id: 10,
    name: "Gizem Ö.",
    rating: 5,
    text: "Dekorasyon çok şık, fotoğraf çekmek için de güzel bir mekan. Tabii kahveler de bir o kadar iyi.",
    date: "4 gün önce"
  }
];

// Features data
const FEATURES = [
  {
    icon: Star,
    title: "4.9",
    subtitle: "Google Puanı",
    description: "113 müşteri değerlendirmesi"
  },
  {
    icon: MapPin,
    title: "Merkezi Konum",
    subtitle: "Çankaya / Ayrancı",
    description: "Kolayca ulaşılabilir"
  },
  {
    icon: Clock,
    title: "00:00'a Kadar",
    subtitle: "Açık",
    description: "Gece geç saatlere kadar"
  }
];

// Star Rating Component
const StarRating = ({ rating, size = 16 }) => {
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={size}
          className={i < rating ? "fill-[#D4A373] text-[#D4A373]" : "text-[#57534E]"}
        />
      ))}
    </div>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="hero-section relative" data-testid="hero-section">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Rott Coffee Co"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 w-full">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="max-w-2xl"
          >
            {/* Badge */}
            <motion.div variants={fadeInUp} className="mb-8">
              <span className="inline-flex items-center gap-2 px-4 py-2 glass-card rounded-full text-sm text-[#D4A373]">
                <Coffee size={16} />
                <span>Premium Kahve Deneyimi</span>
              </span>
            </motion.div>

            {/* Main Title */}
            <motion.h1
              variants={fadeInUp}
              className="font-heading text-5xl md:text-7xl font-bold tracking-tight leading-none mb-6"
              data-testid="hero-title"
            >
              <span className="text-[#F5F5F4]">Rott</span>{" "}
              <span className="text-gradient">Coffee Co</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={fadeInUp}
              className="text-xl md:text-2xl text-[#A8A29E] mb-4 font-light"
              data-testid="hero-subtitle"
            >
              {BUSINESS_INFO.tagline}
            </motion.p>

            {/* Rating Badge */}
            <motion.div variants={fadeInUp} className="flex items-center gap-3 mb-10">
              <StarRating rating={5} size={20} />
              <span className="text-[#F5F5F4] font-semibold">{BUSINESS_INFO.rating}</span>
              <span className="text-[#57534E]">({BUSINESS_INFO.reviewCount} yorum)</span>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Button
                asChild
                className="btn-primary rounded-full px-8 py-6 text-lg font-medium"
                data-testid="directions-btn"
              >
                <Link to="/menu">
                  <Coffee className="mr-2" size={20} />
                  Menüyü İncele
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="btn-outline rounded-full px-8 py-6 text-lg font-medium bg-transparent"
                data-testid="call-btn"
              >
                <a href={`tel:${BUSINESS_INFO.phone.replace(/\s/g, "")}`}>
                  <Phone className="mr-2" size={20} />
                  Bizi Ara
                </a>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <div className="flex flex-col items-center gap-2 text-[#57534E]">
          <span className="text-sm tracking-widest uppercase">Keşfet</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <ChevronRight className="rotate-90" size={24} />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section className="py-24 md:py-32 bg-[#0C0A09]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 md:order-1"
          >
            <div className="relative">
              <img
                src={IMAGES.about}
                alt="Özenle hazırlanmış kahve"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 glass-card p-6 gold-glow">
                <p className="font-accent text-2xl text-[#D4A373]">Est. 2020</p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 md:order-2"
          >
            <span className="text-sm tracking-widest uppercase text-[#D4A373] mb-4 block">
              Hakkımızda
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight mb-8 text-[#F5F5F4]">
              Her Fincanda<br />
              <span className="text-gradient">Tutku ve Özen</span>
            </h2>
            <div className="space-y-6 text-[#A8A29E] text-base md:text-lg leading-relaxed">
              <p>
                Rott Coffee Co olarak, kahve tutkumuzu sizinle paylaşmaktan mutluluk duyuyoruz.
                Ayrancı'nın kalbinde, özenle seçilmiş çekirdeklerden hazırladığımız kahvelerimizle
                sizleri bekliyoruz.
              </p>
              <p>
                Her fincanımızda kalite, her servisimizde samimiyet var. Modern ve şık
                atmosferimizde, günün her saatinde kendinizi evinizde hissedin.
              </p>
            </div>
            <Separator className="my-8 bg-[#292524]" />
            <div className="flex items-center gap-6">
              <div>
                <p className="text-3xl font-heading font-bold text-[#D4A373]">113+</p>
                <p className="text-sm text-[#57534E]">Mutlu Müşteri</p>
              </div>
              <div className="w-px h-12 bg-[#292524]" />
              <div>
                <p className="text-3xl font-heading font-bold text-[#D4A373]">4.9</p>
                <p className="text-sm text-[#57534E]">Google Puanı</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section className="py-24 md:py-32 bg-[#1C1917]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-sm tracking-widest uppercase text-[#D4A373] mb-4 block">
            Neden Biz?
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-[#F5F5F4]">
            Öne Çıkan Özellikler
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {FEATURES.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className="feature-card bg-[#0C0A09] border-[#292524] h-full" data-testid={`feature-card-${index}`}>
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-full bg-[#D4A373]/10 flex items-center justify-center mb-6">
                    <feature.icon className="text-[#D4A373]" size={28} />
                  </div>
                  <h3 className="font-heading text-2xl md:text-3xl font-medium text-[#F5F5F4] mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-[#D4A373] font-medium mb-2">{feature.subtitle}</p>
                  <p className="text-[#57534E]">{feature.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = () => {
  const scrollRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;
    const scrollSpeed = 0.2;

    const scroll = () => {
      if (scrollContainer) {
        scrollPosition += scrollSpeed;

        // Reset position when reaching halfway (for infinite loop effect)
        if (scrollPosition >= scrollContainer.scrollWidth / 2) {
          scrollPosition = 0;
        }

        scrollContainer.scrollLeft = scrollPosition;
      }
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  // Duplicate reviews for infinite scroll effect
  const duplicatedReviews = [...REVIEWS, ...REVIEWS];

  return (
    <section className="py-24 md:py-32 bg-[#0C0A09]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
        >
          <div>
            <span className="text-sm tracking-widest uppercase text-[#D4A373] mb-4 block">
              Müşteri Yorumları
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-[#F5F5F4]">
              Ne Diyorlar?
            </h2>
          </div>
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <img
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
              alt="Google"
              className="h-6 opacity-80"
            />
            <span className="text-[#A8A29E]">üzerinden</span>
          </div>
        </motion.div>

        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-hidden"
          data-testid="reviews-carousel"
        >
          {duplicatedReviews.map((review, index) => (
            <motion.div
              key={`${review.id}-${index}`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="flex-shrink-0 w-[320px] md:w-[380px]"
            >
              <Card className="glass-card border-[#292524] h-full hover:border-[#D4A373]/30 transition-colors duration-300" data-testid={`review-card-${index}`}>
                <CardContent className="p-6">
                  <StarRating rating={review.rating} />
                  <p className="text-[#F5F5F4] mt-4 mb-6 leading-relaxed text-sm md:text-base">
                    "{review.text}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-[#F5F5F4]">{review.name}</p>
                      <p className="text-sm text-[#57534E]">{review.date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Location Section
const LocationSection = () => {
  return (
    <section className="py-24 md:py-32 bg-[#1C1917]" data-testid="location-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm tracking-widest uppercase text-[#D4A373] mb-4 block">
              Konum & İletişim
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-[#F5F5F4] mb-12">
              Bizi Ziyaret Edin
            </h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {/* Address */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-[#D4A373]/10 flex items-center justify-center mb-4">
                  <MapPin className="text-[#D4A373]" size={24} />
                </div>
                <p className="font-medium text-[#F5F5F4] mb-2">Adres</p>
                <p className="text-[#A8A29E] text-sm">{BUSINESS_INFO.address}</p>
              </div>

              {/* Phone */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-[#D4A373]/10 flex items-center justify-center mb-4">
                  <Phone className="text-[#D4A373]" size={24} />
                </div>
                <p className="font-medium text-[#F5F5F4] mb-2">Telefon</p>
                <a
                  href={`tel:${BUSINESS_INFO.phone.replace(/\s/g, "")}`}
                  className="text-[#D4A373] hover:text-[#E5B88F] transition-colors"
                  data-testid="phone-link"
                >
                  {BUSINESS_INFO.phone}
                </a>
              </div>

              {/* Instagram */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-[#D4A373]/10 flex items-center justify-center mb-4">
                  <Instagram className="text-[#D4A373]" size={24} />
                </div>
                <p className="font-medium text-[#F5F5F4] mb-2">Instagram</p>
                <a
                  href={BUSINESS_INFO.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#D4A373] hover:text-[#E5B88F] transition-colors"
                  data-testid="instagram-link"
                >
                  @rottcoffeeco
                </a>
              </div>

              {/* Hours */}
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-[#D4A373]/10 flex items-center justify-center mb-4">
                  <Clock className="text-[#D4A373]" size={24} />
                </div>
                <p className="font-medium text-[#F5F5F4] mb-2">Çalışma Saatleri</p>
                <p className="text-[#A8A29E] text-sm">{BUSINESS_INFO.hours}</p>
              </div>
            </div>

            <Button
              asChild
              className="btn-primary rounded-full px-8 py-6 text-lg font-medium"
              data-testid="location-directions-btn"
            >
              <a href={BUSINESS_INFO.googleMapsUrl} target="_blank" rel="noopener noreferrer">
                <Navigation className="mr-2" size={20} />
                Yol Tarifi Al
              </a>
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="py-12 bg-[#0C0A09] border-t border-[#292524]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          {/* Logo & Brand */}
          <div>
            <h3 className="font-heading text-2xl font-bold text-[#F5F5F4] mb-2">
              Rott <span className="text-[#D4A373]">Coffee Co</span>
            </h3>
            <p className="text-[#57534E] text-sm">
              Ayrancı'da Nitelikli Kahve Deneyimi
            </p>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col md:flex-row gap-6 md:gap-12 text-sm">
            <div>
              <p className="text-[#57534E] mb-1">Adres</p>
              <p className="text-[#A8A29E]">Şht. Mahir Turan Sk. No:8</p>
              <p className="text-[#A8A29E]">Çankaya / Ankara</p>
            </div>
            <div>
              <p className="text-[#57534E] mb-1">İletişim</p>
              <div className="flex flex-col gap-1">
                <a
                  href={`tel:${BUSINESS_INFO.phone.replace(/\s/g, "")}`}
                  className="text-[#D4A373] hover:text-[#E5B88F] transition-colors"
                  data-testid="footer-phone"
                >
                  {BUSINESS_INFO.phone}
                </a>
                <a
                  href={BUSINESS_INFO.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#A8A29E] hover:text-[#D4A373] transition-colors flex items-center gap-1"
                  data-testid="footer-instagram"
                >
                  <Instagram size={14} /> @rottcoffeeco
                </a>
              </div>
            </div>
          </div>
        </div>

        <Separator className="my-8 bg-[#292524]" />

        {/* Copyright & Admin Link */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-sm text-[#57534E]">
          <p>© {currentYear} Rott Coffee Co. Tüm hakları saklıdır.</p>
          <div className="flex items-center gap-6">
            <Link to="/admin" className="hover:text-[#D4A373] transition-colors flex items-center gap-1">
              Yönetim Paneli <ChevronRight size={14} />
            </Link>
            <p className="flex items-center gap-1">
              <Coffee size={14} className="text-[#D4A373]" />
              <span>Özenle hazırlanmıştır</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-[#0C0A09]" data-testid="landing-page-container">
      <header className="fixed top-0 w-full z-50 bg-[#0C0A09]/80 backdrop-blur-md border-b border-[#292524] py-4">
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/rott_logo_new_v2.jpg" alt="Rott Coffee Logo" className="w-10 h-10 rounded-full border border-[#D4A373]/30" />
            <div className="font-heading font-bold text-[#F5F5F4] text-xl">
              Rott <span className="text-[#D4A373]">Coffee Co</span>
            </div>
          </Link>
          <div className="flex items-center gap-6">
            <a href="#hero-section" className="hidden md:block text-[#A8A29E] hover:text-[#F5F5F4] transition-colors">Ana Sayfa</a>
            <a href="#about-section" className="hidden md:block text-[#A8A29E] hover:text-[#F5F5F4] transition-colors">Hakkımızda</a>
            <Link to="/menu" className="hidden md:block text-[#A8A29E] hover:text-[#F5F5F4] transition-colors">Menü</Link>
            <a href="#reviews-section" className="hidden md:block text-[#A8A29E] hover:text-[#F5F5F4] transition-colors">Yorumlar</a>
            <a href="#location-section" className="hidden md:block text-[#A8A29E] hover:text-[#F5F5F4] transition-colors">İletişim</a>
          </div>
        </div>
      </header>
      <HeroSection />
      <AboutSection />
      <FeaturesSection />
      <ReviewsSection />
      <LocationSection />
      <Footer />
    </div>
  );
};

// Main App Component
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/menu" element={<MenuPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
